/**
 * GH#2403 — «Mis Propiedades» del agente se caía con una sola propiedad.
 *
 * El módulo del agente se escribió contra un modelo ANIDADO (`property.dimensions.area`, `price: { amount, currency }`,
 * `code`, `type`) que el API nunca devolvió: `GET /properties` entrega la entidad PLANA, igual que la leen las demás
 * pantallas de propiedades (agency, properties-shared, seller, inventory). Forma medida en producción:
 *
 *   coveredAreaM2 "120.50"  (TEXTO numérico: columna numeric de Postgres)   bedrooms 3  bathrooms 2  parkingSpaces 1
 *   landSize null  size null  floors null                                   price "250000.00" (TEXTO)  currency "USD"
 *   propertyType "apartment"  referenceCode null  slug "…"                  location { address, city, state, neighborhood… }
 *   (sin `dimensions`, sin `code`, sin `type`, sin `location.zone`)
 *
 * Estas funciones leen de lo que el API SÍ trae (y, si algún día llega el modelo anidado, de ahí), aceptan número o
 * texto numérico y devuelven `null`/'' cuando falta, para que ninguna pantalla se caiga por un campo ausente.
 */
type Cru = Record<string, any> | null | undefined;

/** Número finito o null: acepta `120.5` y `"120.50"`; un texto vacío, NaN o un objeto no son número. */
export const numero = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const primero = (...candidatos: unknown[]): number | null => {
  for (const c of candidatos) {
    const n = numero(c);
    if (n !== null) return n;
  }
  return null;
};

export interface Caracteristicas {
  area: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  parking: number | null;
  floors: number | null;
}

/** Área, habitaciones, baños, parqueos y pisos: primero lo que trae el API (plano) y después `dimensions`. */
export const caracteristicasDe = (p: Cru): Caracteristicas => {
  const d = (p && typeof p.dimensions === 'object' && p.dimensions) || {};
  return {
    area: primero(p?.coveredAreaM2, p?.size, p?.landSize, d.area),
    bedrooms: primero(p?.bedrooms, d.bedrooms),
    bathrooms: primero(p?.bathrooms, d.bathrooms),
    parking: primero(p?.parkingSpaces, d.parking, d.parkingSpaces),
    floors: primero(p?.floors, d.floors),
  };
};

/** Lo que se muestra cuando el dato falta: un guion, nunca «undefined» ni «NaN». */
export const valorODash = (v: number | null | undefined, sufijo = ''): string =>
  v === null || v === undefined || !Number.isFinite(v) ? '—' : `${v}${sufijo}`;

export interface PrecioLeido {
  amount: number;
  currency: string;
  period?: 'monthly' | 'yearly' | 'one-time';
  onRequest?: boolean;
}

/** `price` llega como texto decimal ("250000.00") con `currency` aparte; el modelo viejo lo esperaba como objeto. */
export const precioDe = (p: Cru): PrecioLeido => {
  const pr = p?.price;
  const moneda = (typeof p?.currency === 'string' && p.currency) || 'USD';
  if (pr && typeof pr === 'object') {
    return { ...pr, amount: numero((pr as any).amount) ?? NaN, currency: (pr as any).currency || moneda };
  }
  return { amount: numero(pr) ?? NaN, currency: moneda };
};

const texto = (v: unknown): string => (typeof v === 'string' ? v : v === null || v === undefined ? '' : String(v));

/** `code` no existe en el API: el identificador que sí trae es `referenceCode` y, si falta, `slug`. */
export const codigoDe = (p: Cru): string => texto(p?.code ?? p?.referenceCode ?? p?.slug);
export const tituloDe = (p: Cru): string => texto(p?.title);
export const ciudadDe = (p: Cru): string => texto(p?.location?.city);
/** `location.zone` no existe en el API; el barrio llega como `neighborhood`. */
export const zonaDe = (p: Cru): string => texto(p?.location?.zone ?? p?.location?.neighborhood);
/** El API entrega `propertyType`; el modelo viejo esperaba `type`. */
export const tipoDe = (p: Cru): string => texto(p?.type ?? p?.propertyType);

/**
 * GH#2435 — el API entrega el responsable como objeto `agent` ({ id, fullName, email }); el modelo viejo esperaba
 * `agentName` y `agentId` planos y la ficha decía «ID:» y nada. `nombre` queda '' si no hay (la pantalla usa el id solo entonces).
 */
export const agenteDe = (p: Cru): { nombre: string; id: string } => {
  const a: Record<string, any> = p?.agent && typeof p.agent === 'object' ? p.agent : {};
  return { nombre: texto(a.fullName || p?.agentName).trim(), id: texto(a.id ?? p?.agentId).trim() };
};

const sinTilde = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

/**
 * GH#2435 — las propiedades creadas con el alta del agente se guardaron como «Republica Dominicana» (sin tilde): la ficha
 * lo muestra bien aunque el dato guardado no lo esté. Cualquier otro país pasa tal cual.
 */
export const paisDe = (p: Cru): string => {
  const pais = texto(p?.location?.country);
  return sinTilde(pais) === 'republica dominicana' ? 'República Dominicana' : pais;
};
